import React, { useMemo, useState } from "react";
import CountryFlag from "../CountryFlag";
import { ConnectBody, ConnectFoot, PrimaryButton } from "./ConnectScreen";
import { popularCountries, searchCountries } from "../../lib/countryPicker";
import type { SupportedCountry } from "../../lib/supportedCountries";
import { t } from "../../i18n";
import { T } from "../../i18n/T";

// Step 1 of "Connect your bank", full screen (founder 2026-10-19): their
// country already selected at the top (where their connection is, or the one
// they picked before), the founder's popular countries, every country A–Z,
// and a search over all of them. Tapping any country moves on; Continue moves
// on with the selected one. A country we can't pay yet says "Not yet".

const Chev = () => (
  <svg className="cx-row-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
);
const Check = () => (
  <svg className="cx-row-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
export const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.6-3.6" /></svg>
);
const ClearIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor" /><path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="#161a26" strokeWidth="2.2" strokeLinecap="round" /></svg>
);

function Row({ c, selected, onPick, disabled }: { c: SupportedCountry; selected?: boolean; onPick: (c: SupportedCountry) => void; disabled?: boolean }) {
  return (
    <button type="button" className={`cx-row${selected ? " is-selected" : ""}`} onClick={() => onPick(c)} disabled={disabled} data-code={c.code}>
      <CountryFlag code={c.code} />
      <span className="cx-row-name">{c.name}</span>
      {!c.open ? <span className="cx-tag">{t("connectBank.notYet")}</span> : selected ? <Check /> : <Chev />}
    </button>
  );
}

export default function CountryPicker(props: {
  /** Every listed country, A–Z. */
  countries: SupportedCountry[];
  /** Shown selected at the top: the one picked before, else the talent's location. */
  selectedCode?: string | null;
  onPick: (c: SupportedCountry) => void;
  pending?: boolean;
  error?: string;
}) {
  const { countries, onPick, pending } = props;
  const [query, setQuery] = useState("");
  const selected = countries.find((c) => c.code === props.selectedCode) || null;
  const searching = !!query.trim();
  const results = useMemo(() => searchCountries(countries, query), [countries, query]);
  const popular = useMemo(() => popularCountries(countries, selected?.code), [countries, selected]);
  const row = (c: SupportedCountry) => <Row key={c.code} c={c} selected={c.code === selected?.code} onPick={onPick} disabled={pending} />;

  return (
    <>
      <ConnectBody>
        <h2 id="cx-title" className="cx-title"><T k="payoutRailCard.whereIsYourBank" tags={{ 0: <br /> }} /></h2>
        {!searching && <p className="cx-sub">{t("connectBank.thisSetsHowYou")}</p>}
        <label className="cx-search">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("connectBank.searchAllCountries")}
            aria-label={t("connectBank.searchAllCountries")}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          {query && (
            <button type="button" className="cx-clear" onClick={() => setQuery("")} aria-label={t("connectBank.clearSearch")}><ClearIcon /></button>
          )}
        </label>
        {props.error && <p className="cx-error" role="alert">{props.error}</p>}
        {searching ? (
          results.length ? results.map(row) : <p className="cx-empty">{t("connectBank.noCountryFound")}</p>
        ) : (
          <>
            {selected && row(selected)}
            {popular.length > 0 && <p className="cx-sec">{t("connectBank.popularCountries")}</p>}
            {popular.map(row)}
            <p className="cx-sec">{t("connectBank.allCountries")}</p>
            {countries.map(row)}
          </>
        )}
      </ConnectBody>
      {selected && !searching && (
        <ConnectFoot>
          <PrimaryButton onClick={() => onPick(selected)} disabled={pending} busy={pending}>{t("connectBankSheet.continue")}</PrimaryButton>
        </ConnectFoot>
      )}
    </>
  );
}
